import React, { useState, useRef } from "react";
import { Box, Typography, Paper, Popper, Fade } from "@mui/material";
import { ThumbsUp } from "lucide-react";
import { Avatar } from "./Avatar";
import { getAvatarImageUrl } from "../utils/avatarUtils";

export const LikersTooltip = ({ likers = [], children }) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);
  const closeTimer = useRef(null);

  if (!likers?.length) return <>{children}</>;

  const handleMouseEnter = () => {
    clearTimeout(closeTimer.current);
    setOpen(true);
  };

  const handleMouseLeave = () => {
    closeTimer.current = setTimeout(() => setOpen(false), 100);
  };

  return (
    <Box
      ref={anchorRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      sx={{ display: "inline-flex" }}
    >
      {children}

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        transition
        style={{ zIndex: 1400 }}
        modifiers={[{ name: "flip", enabled: true }, { name: "preventOverflow", enabled: true }]}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={150}>
            <Paper
              elevation={4}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              sx={{
                mt: "6px",
                minWidth: 180,
                maxWidth: 240,
                borderRadius: "10px",
                overflow: "hidden",
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              {/* Header */}
              <Box
                sx={{
                  px: 1.5,
                  pt: 1,
                  pb: 0.5,
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                  borderBottom: "1px solid",
                  borderColor: "divider",
                }}
              >
                <ThumbsUp size={12} />
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 600, fontSize: "0.7rem", color: "text.secondary" }}
                >
                  {likers.length} {likers.length === 1 ? "like" : "likes"}
                </Typography>
              </Box>

              {/* Liker list */}
              <Box sx={{ py: "4px" }}>
                {likers.map((user, idx) => {
                  const avatarUrl = getAvatarImageUrl(user.profile_image);
                  return (
                    <Box
                      key={idx}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        px: 1.5,
                        py: "5px",
                        "&:hover": { bgcolor: "action.hover" },
                      }}
                    >
                      <Avatar
                        src={avatarUrl}
                        name={user.full_name}
                        size="sm"
                        sx={{ flexShrink: 0 }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: "0.8rem",
                          fontWeight: 500,
                          color: "text.primary",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {user.full_name || "Usuario"}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Paper>
          </Fade>
        )}
      </Popper>
    </Box>
  );
};
